import { isEmpty } from "lodash";
import React, { useEffect, useState, useRef, useMemo } from "react";
import Joyride, { STATUS } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Grid from "@mui/material/Grid";

import DeleteModal from "../../components/DeleteModal";
import ReportModal from "../../components/ReportModal";
import { BusinessBoxDefaultData } from "../../components/WheelGallery/BusinessBox";
import DataBox from "../../components/WheelGallery/BusinessBox/DataBox";
import GallerySettings from "../../components/WheelGallery/GallerySettings";
import GalleryHeader from "../../components/WheelGallery/Header/GalleryHeader";
import ProfilePopup from "../../components/WheelGallery/Profile/ProfilePopup";
import WheelListing from "../../components/WheelGallery/WheelListing";
import Move from "../../components/WheelGallery/WheelListing/Folders/Components/Move";
import GalleryTooltip from "../../components/WheelGallery/GalleryTooltip";
import { galleryTourSteps } from "../../components/WheelGallery/GalleryTooltip/steps";
import GallerySkeleton from "../../components/Gallery/GallerySkeleton";

import {
  editUserProfileAction,
  getUserProfileByIdAction,
} from "../../redux/actions";
import {
  resetFolders,
  selectIsUserProfile,
  selectPrimaryWheel,
  setIsUserProfile,
} from "../../redux/reducers/gallery";
import { getLoggedInUser } from "../../utils/helpers";
import { SocialIcons } from "../../utils/socialHelpers";
import { colors } from "../../utils";

import DefaultIyugaLogo from "@assets/images/iyuga.jpg";

import _ from "lodash";
import CreateWheelPopup from "../../components/CreateWheelPopup";
import LegacyWheelModal from "../../components/WheelGallery/WheelListing/ListComponents/LegacyWheelModal";
import useScreenSize from "../../hooks/useScreenSize";
import {
  useGetUserProfileById,
  useGetPrimaryWheelV2,
} from "../../services/queries";
import { Button, TextView } from "../../components";
import { useUserUpdateProfile } from "../../services/mutations";
import ContentModal from "../../components/ContentModeration/ContentModal";
import DMCATakedown from "../../components/ContentModeration/Modals/DMCATakedown";
import DMCACounter from "../../components/ContentModeration/Modals/DMCACounter";
import ContentRemovalDispute from "../../components/ContentModeration/Modals/ContentRemovalDispute";
import AIWheelProgress from "../../components/AIWheelProgress";
import { useIfUserEligible } from "../../hooks/useIfUserEligible";
import { FEATURES } from "../../constants/features";
import ConfirmationModal from "../../components/SegmentBuilder/Main/BusinessBoxBuilder/children/ConfirmationModal";
import { Dialog, DialogContent, styled, Fade } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { closeModal } from "../../redux/reducers/dialog";
import { getLatestWheelToShowInReferModal } from "../../services/api";
import NonAuthenticatedModal from "../../components/NonAuthenticatedModal";
import Popup from "../../components/MobileView/Popup";
import GeneralDialog from "../../components/GeneralDialogBox";
import { closeGeneralDialogModal } from "../../redux/reducers/generalDialog";
import { closeNonAuthenticatedModal } from "../../redux/reducers/nonAuthenticatedModal";
import MobileModal from "../../components/Gallery/MobileModal";

const ButtonContainer = styled("div")(() => ({
  display: "flex",
  gap: "16px",
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  alignItems: "center",
}));

const Gallery = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isMobile } = useScreenSize();
  const userEdit = useUserUpdateProfile();
  const headerRef = useRef();

  const { userProfile, loading: userProfileLoading } = useSelector(
    (state) => state.userProfile,
  );
  const aiWheels = useSelector((state) => state.aiWheels.wheels);

  const {
    data: profileData,
    isLoading: profileDataLoading,
    isFetching: profileDataFetching,
  } = useGetUserProfileById(id, { enabled: !isMobile && !!id });

  // Fetch profile data for WheelListing component
  const {
    data: wheelListingProfileData,
    isFetching: wheelListingProfileFetching,
  } = useGetUserProfileById(id || getLoggedInUser()?.id, {
    enabled: !isMobile && !!(id || getLoggedInUser()?.id),
  });

  // Fetch primary wheel data for WheelListing component
  const { data: primaryWheelData, isLoading: primaryWheelLoading } =
    useGetPrimaryWheelV2(id || getLoggedInUser()?.id, {
      enabled: !isMobile && !!(id || getLoggedInUser()?.id),
    });

  const isUserProfile = useSelector(selectIsUserProfile);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.BUSINESS_BOX, { enabled: !isMobile });

  console.log(
    "Gallery SUBSCRIPTION DATA:",
    "Business box eligibility:",
    isEligible,
    "isUserProfile:",
    isUserProfile,
  );

  const bboxData = useSelector((state) =>
    !isEmpty(state.gallery.profile?.business_box)
      ? state.gallery?.profile?.business_box
      : null,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gallerySettingsOpen, setGallerySettingsOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [isJoyrideMenuOpen, setIsJoyrideMenuOpen] = useState(false);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [openLibraryInBusinessBox, setOpenLibraryInBusinessBox] =
    useState(false);
  const [showExplorePopup, setShowExplorePopup] = useState(false);
  const [wheelToRender, setWheelToRender] = useState(null);
  const [myWheelToRender, setMyWheelToRender] = useState(null);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const wheelUpdatedData = useMemo(
    () =>
      wheelToRender?.wheel_segments?.length > 0
        ? wheelToRender.wheel_segments.map((obj) => ({ ...obj }))
        : [],
    [wheelToRender?.wheel_segments],
  );

  const wheelUpdatedDataSorted = useMemo(
    () =>
      wheelUpdatedData?.length > 0
        ? [...wheelUpdatedData].sort((a, b) => a.position - b.position)
        : [],
    [wheelUpdatedData],
  );

  // build a sorted segments array for the LOGGED-IN user's wheel
  const myWheelUpdatedData = useMemo(
    () =>
      myWheelToRender?.wheel_segments?.length > 0
        ? myWheelToRender.wheel_segments.map((obj) => ({ ...obj }))
        : [],
    [myWheelToRender?.wheel_segments],
  );

  const myWheelUpdatedDataSorted = useMemo(
    () =>
      myWheelUpdatedData.length > 0
        ? [...myWheelUpdatedData].sort((a, b) => a.position - b.position)
        : [],
    [myWheelUpdatedData],
  );

  const primaryWheel = useSelector(selectPrimaryWheel);

  const handleJoyrideCallback = (data) => {
    const { status, index, type } = data;

    // Open profile menu when reaching its step
    if (type === "step:before" && index === galleryTourSteps.length - 1) {
      if (headerRef.current?.openProfileMenu) {
        setIsJoyrideMenuOpen(true);
        headerRef.current.openProfileMenu();
      }
    }

    // Close profile menu when tour ends or is skipped
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setIsJoyrideMenuOpen(false);
      if (headerRef.current?.closeProfileMenu) {
        headerRef.current.closeProfileMenu();
      }

      // Update user profile to mark walkthrough as completed
      if (userProfile) {
        if (doNotShowAgain) {
          userEdit.mutate(
            {
              show_walkthrough: false,
            },
            {
              onSuccess: (response) => {
                if (response?.status === 200) {
                  dispatch(
                    editUserProfileAction({
                      ...userProfile,
                      show_walkthrough: false,
                    }),
                  );
                }
              },
            },
          );
        }
        localStorage.setItem("show_walkthrough", "false");
      }
      setRunTour(false);
    }
  };

  useEffect(() => {
    if (isMobile) return;

    const loggedInUserId = getLoggedInUser()?.id;

    dispatch(getUserProfileByIdAction(loggedInUserId));
    dispatch(setIsUserProfile(true));

    return () => {
      dispatch(setIsUserProfile(false));
      dispatch(resetFolders());
    };
  }, [dispatch, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    if (id) {
      const loggedInUserId = getLoggedInUser()?.id;

      dispatch(resetFolders());
      dispatch(setIsUserProfile(loggedInUserId == id));
    }
  }, [dispatch, id, isMobile]);

  useEffect(() => {
    setIsModalOpen(isMobile);
  }, [isMobile]);

  useEffect(() => {
    const showWalkthrough = localStorage.getItem("show_walkthrough");
    if (userProfile) {
      const { first_name, last_name, code, show_walkthrough } = userProfile;

      setRunTour(
        showWalkthrough == "true" && show_walkthrough == true ? true : false,
      );

      const referralData = sessionStorage.getItem("referralData");
      if (!referralData || referralData.referral_code !== code) {
        sessionStorage.setItem(
          "referralData",
          JSON.stringify({ first_name, last_name, referral_code: code }),
        );
      }
    }
    if (profileData) {
      const { first_name, last_name, code, is_in_referral_program } =
        profileData?.data;
      const referralData = sessionStorage.getItem("referralData");

      if (
        (!referralData || referralData.referral_code !== code) &&
        is_in_referral_program
      ) {
        sessionStorage.setItem(
          "referralData",
          JSON.stringify({ first_name, last_name, referral_code: code }),
        );
      }
    }
  }, [userProfile, profileData]);

  const showData = () => {
    if (
      profileData?.data?.visibility === "PUBLIC" ||
      userProfile?.user_id == id
    ) {
      return true;
    }
    if (
      profileData?.data?.visibility === "PRIVATE" &&
      !_.isEmpty(profileData?.data)
    ) {
      return true;
    }

    return false;
  };

  const handleEditBusinessBoxClick = () => {
    setConfirmationModalOpen(true);
  };

  const handleBusinessBoxEditorOpen = (openLibrary = false) => {
    setOpenLibraryInBusinessBox(openLibrary);

    setGallerySettingsOpen(true);
  };

  const handleReplaceBusinessBoxClick = () => {
    handleBusinessBoxEditorOpen(true); // Pass true to open library section
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlOption = urlParams.get("option");

    if (urlOption === "explorePage") {
      setShowExplorePopup(true);
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Just dispatch close modal action
      // We don't need to prevent default here as we want the navigation to proceed
      dispatch(closeModal());
      dispatch(closeGeneralDialogModal());
      dispatch(closeNonAuthenticatedModal());
    };

    const handlePopState = () => {
      dispatch(closeModal());
      dispatch(closeGeneralDialogModal());
      dispatch(closeNonAuthenticatedModal());
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Clean up
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dispatch]);

  useEffect(() => {
    if (isMobile) return;

    const getWheelToRender = async () => {
      const response = await getLatestWheelToShowInReferModal(
        profileData?.data?.user_id,
      );
      if (response?.data?.data?.id) {
        console.log(response?.data?.data, "response");

        setWheelToRender(response.data.data);
        return;
      }
      setWheelToRender(null);
    };

    if (profileData?.data?.user_id && !isUserProfile) {
      getWheelToRender();
    }
  }, [profileData?.data?.user_id, primaryWheel, isUserProfile, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    const loggedInUserId = getLoggedInUser()?.id;
    if (!loggedInUserId) {
      setMyWheelToRender(null);
      return;
    }

    const fetchMyLatest = async () => {
      const response = await getLatestWheelToShowInReferModal(loggedInUserId);
      if (response?.data?.data?.id) {
        setMyWheelToRender(response.data.data);
        return;
      }
      setMyWheelToRender(null);
    };

    fetchMyLatest();
    // re-run if primary wheel changes (e.g., user updates it)
  }, [primaryWheel, isMobile]);

  // Track when initial loading is complete
  // Show skeleton until Profile data is ready for WheelListing to render
  useEffect(() => {
    // On mobile, skip loading and set complete immediately
    if (isMobile) {
      setInitialLoadComplete(true);
      return;
    }

    // For user's own profile, wait for userProfile to load
    if (!id) {
      if (
        userProfile &&
        !userProfileLoading &&
        !wheelListingProfileFetching &&
        !primaryWheelLoading &&
        !isCheckingEligibility
      ) {
        setInitialLoadComplete(true);
      }
    } else {
      // For viewing another user's profile, wait for profileData to load
      // This ensures WheelListing can render Profile and PrimaryWheel components
      if (
        profileData &&
        !profileDataLoading &&
        !profileDataFetching &&
        !wheelListingProfileFetching &&
        !primaryWheelLoading &&
        !isCheckingEligibility
      ) {
        setInitialLoadComplete(true);
      }
    }
  }, [
    id,
    userProfile,
    userProfileLoading,
    profileData,
    profileDataLoading,
    profileDataFetching,
    wheelListingProfileFetching,
    primaryWheelLoading,
    isCheckingEligibility,
    isMobile,
  ]);

  // Show skeleton while initial data is loading
  if (!initialLoadComplete) {
    return <GallerySkeleton />;
  }

  return (
    <>
      <Helmet>
        <title>iYuga</title>
        <meta name="description" content={"dummy description"} />
        <meta
          property="og:image"
          content={
            "https://iyuga-backend.s3.amazonaws.com/uploads/abd220ce-a550-4469-b314-c0e0d50a5548-iYuga%20image.png"
          }
        />
        <meta property="og:title" content="iYuga" />
        <meta property="og:site_name" content="iYuga" />
        <meta property="og:url" content="https://iyuga.net" />
        <meta property="og:type" content="website" />
        <meta property="og:description" content={""} />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:title" content={"iYuga"} />
        <meta property="twitter:description" content={""} />
        <meta
          property="twitter:image"
          content={
            "https://iyuga-backend.s3.us-east-1.amazonaws.com/wheel-share-image.jpeg"
          }
        />
      </Helmet>
      {!isMobile && (
        <>
          <Joyride
            steps={galleryTourSteps}
            run={runTour}
            continuous
            showProgress
            showSkipButton
            disableOverlayClose
            disableOverlay={false}
            spotlightClicks={false}
            hideBackButton={false}
            tooltipComponent={(props) => (
              <GalleryTooltip
                {...props}
                doNotShowAgain={doNotShowAgain}
                onDoNotShowAgainChange={() =>
                  setDoNotShowAgain(!doNotShowAgain)
                }
              />
            )}
            callback={handleJoyrideCallback}
            styles={{
              options: {
                zIndex: 10000,
                primaryColor: colors.PrimaryPurple,
                backgroundColor: "#ffffff",
                arrowColor: "#ffffff",
                textColor: "#565556",
                textAlign: "left",
                spotlightShadow: "0 0 15px rgba(18, 48, 81, 0.3)",
                beaconSize: 36,
              },
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                mixBlendMode: "hard-light",
              },
            }}
          />
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: runTour ? 9999 : -1,
              pointerEvents: runTour ? "all" : "none",
              background: "transparent",
            }}
          />
          <Grid sx={{ height: "100vh", backgroundColor: "#E8E8E8" }}>
            {/* Header */}
            <Fade in={true} timeout={600}>
              <Grid
                item
                xs={12}
                sx={{
                  height: "11vh",
                  marginBottom: "1vh",
                }}
              >
                <GalleryHeader
                  ref={headerRef}
                  isUserProfile={isUserProfile}
                  isJoyrideOpen={isJoyrideMenuOpen}
                  inviteUrl={`https://link.iyuga.net/widget/form/KJABk05OJDB1fgLLXd78?referral_first_name=${
                    userProfile?.first_name || ""
                  }&referral_last_name=${userProfile?.last_name || ""}`}
                  wheelUpdatedDataSorted={myWheelUpdatedDataSorted}
                />
              </Grid>
            </Fade>

            {/* Main Content */}
            <div
              style={{
                height: "calc(100vh - 12vh)",
                overflow: "hidden",
                backgroundColor: "#E8E8E8",
                ...(showData() && {
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gridGap: 10,
                }),
              }}
            >
              {/* Sidebar */}
              {showData() && (
                <Fade in={true} timeout={800}>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      marginLeft: "10px",
                      overflow: "auto",
                      maxHeight: "calc(100vh - 12vh)",
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      "-ms-overflow-style": "none",
                      "scrollbar-width": "none",
                    }}
                  >
                    {!isEmpty(bboxData) &&
                    (isUserProfile ? isEligible : true) ? (
                      <DataBox
                        isGalleryBbox
                        onBboxEdit={handleEditBusinessBoxClick}
                        onReplaceClick={handleReplaceBusinessBoxClick}
                        data={{
                          business_title: bboxData.business_title,
                          imageSrc: bboxData.media_url,
                          description: bboxData.description,
                          icons:
                            bboxData.business_link?.map((linkItem, idx) => {
                              return {
                                id: idx,
                                icon: linkItem.icon,
                                link: linkItem.link,
                                is_cta_enabled: linkItem.is_cta_enabled,
                              };
                            }) || [],
                          ctaButtons: bboxData.business_box_cta || [],
                          id: bboxData.id || null,
                        }}
                        isEligible={isUserProfile ? isEligible : true}
                      />
                    ) : (
                      <DataBox
                        data={BusinessBoxDefaultData}
                        isGalleryBbox
                        onBboxEdit={handleEditBusinessBoxClick}
                        onReplaceClick={handleReplaceBusinessBoxClick}
                        isDefault
                        isEligible={isUserProfile ? isEligible : true}
                        profileData={profileData}
                      />
                    )}
                  </Grid>
                </Fade>
              )}

              {/* Main Gallery */}
              <Fade in={true} timeout={800}>
                <Grid
                  id="MainScrollableView"
                  item
                  xs={9}
                  sx={{
                    overflowY: "scroll",
                    height: "calc(100vh - 12vh)",
                  }}
                >
                  <WheelListing
                    wheelUpdatedDataSorted={
                      isUserProfile
                        ? myWheelUpdatedDataSorted
                        : wheelUpdatedDataSorted
                    }
                    profileData={wheelListingProfileData}
                    isFetching={wheelListingProfileFetching}
                    primaryWheelData={primaryWheelData}
                    primaryWheelLoading={primaryWheelLoading}
                  />
                </Grid>
              </Fade>
            </div>
          </Grid>
        </>
      )}

      {/* Modals */}
      {aiWheels.length > 0 && <AIWheelProgress wheels={aiWheels} />}

      <ProfilePopup />
      <DeleteModal />
      <LegacyWheelModal />
      <CreateWheelPopup />

      {/* <Popup
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        disableClose
      /> */}
      <MobileModal
        open={isModalOpen}
        handleClose={() => {
          navigate(-1);
        }}
        disableClose={true}
        title1="Mobile is coming Q4 2025! Please log into your desktop computer to enjoy iYuga's Web app."
        imageSrc={DefaultIyugaLogo}
        wheelData={
          isUserProfile ? myWheelUpdatedDataSorted : wheelUpdatedDataSorted
        }
        profileData={wheelListingProfileData}
      />
      {isUserProfile && (
        <GallerySettings
          open={gallerySettingsOpen}
          onClose={() => setGallerySettingsOpen(false)}
          openEditable
          openDefault={!isEmpty(bboxData)} //opens create new business box if no business box on gallery
          hideTabs
          title="Business Box"
          openLibrarySection={openLibraryInBusinessBox}
        />
      )}
      <Move />
      <ReportModal />
      <GeneralDialog />

      <Dialog
        open={showExplorePopup}
        onClose={() => setShowExplorePopup(false)}
        fullWidth={true}
      >
        <StyledDialogContent>
          <img src={DefaultIyugaLogo} alt="iyuga" width={78} height={74} />
          <TextView variant="Header3" color={"#565556"}>
            Let's get started!
          </TextView>
          <ButtonContainer>
            <Button
              variant={"outlined"}
              style={{ width: "auto" }}
              onClick={() => navigate("/explore")}
            >
              Explore
            </Button>
            <Button
              style={{ width: "auto" }}
              onClick={() => setShowExplorePopup(false)}
            >
              Go To Gallery
            </Button>
          </ButtonContainer>
        </StyledDialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={confirmationModalOpen}
        handleClose={() => setConfirmationModalOpen(false)}
        onConfirm={() => {
          handleBusinessBoxEditorOpen();
        }}
        title="Edit Alert"
        message={
          "Editing this Business Box will update it wherever it's used. Are you sure you wish to proceed?"
        }
      />

      {/* <DMCATakedown
        open
        initialValues={{
          profile_name: `${userProfile?.first_name} ${userProfile?.last_name}`,
          phone_number: `${userProfile?.country_code} ${userProfile?.phone_number}`,
          email: userProfile?.email,
        }}
      /> */}
      {/* <DMCACounter
        open
        initialFormValues={{
          profile_name: `${userProfile?.first_name} ${userProfile?.last_name}`,
          phone_number: `${userProfile?.country_code} ${userProfile?.phone_number}`,
          email: userProfile?.email,
        }}
      /> */}
      {/* <ContentRemovalDispute
        open
        initialFormValues={{
          profile_name: `${userProfile?.first_name} ${userProfile?.last_name}`,
          phone_number: `${userProfile?.country_code} ${userProfile?.phone_number}`,
          email: userProfile?.email,
        }}
      /> */}
      <NonAuthenticatedModal />
    </>
  );
};

export default Gallery;
